import { HelpTip, KpiCard } from '@/components/ui';
import { formatCurrency } from '@/lib/format';
import { useMinhasVendas } from '@/lib/home-today-store';

/**
 * CARD-VENDAS-VENDEDOR (pedido do Matheus, 20/08/2026) — "Minhas vendas em
 * <mês>" na tela inicial do vendedor (Gabriel e Enzo; o mecanismo é geral).
 *
 * Regras do pedido:
 *   - UM número: o total do mês vigente. Sem quebra, sem lista, sem gráfico.
 *   - Cada um vê só o dele — decidido no SERVIDOR (a rota não aceita pedir o
 *     número de outra pessoa; ver kpis.ts).
 *   - Usuário SEM vínculo com vendedor do Omie NÃO vê o card. Zero seria uma
 *     afirmação ("você não vendeu nada"); ausência é a verdade ("não sei quem
 *     você é no Omie"). O widget devolve null e a Home segue inteira.
 *   - Sem aviso de cobertura no card (decisão dele — a consequência está no
 *     relatório da rodada, não aqui).
 *   - Componente canônico (KpiCard), título com o mês por extenso, tooltip
 *     "?" em portal (HelpTip), carregando/erro em português — nunca card
 *     vazio nem zero silencioso.
 */

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function mesPorExtenso(anoMes: string | undefined): string {
  const m = Number((anoMes ?? '').slice(5, 7));
  return MESES[m - 1] ?? 'no mês';
}

const AJUDA =
  'Quanto você vendeu no mês vigente, somando as duas empresas: títulos emitidos neste mês no ' +
  'Omie com você como vendedor, nas cinco categorias de venda (produtos e serviços pontuais do ' +
  'Dp. Técnico, faturamento direto de produtos do Dp. Técnico, manutenção de equipamentos e ' +
  'plantão). É a mesma regra de data do Relatório de Vendas, e o número atualiza junto com a ' +
  'sincronização do Omie. Detalhes na Central de Ajuda, artigo "O card Minhas vendas".';

/**
 * VENDAS OPERACIONAIS — D1 (08/10/2026): quem tem a regra técnica (hoje o Enzo; o servidor decide e devolve
 * `regra`) lê o que conta no card dele. A frase do meio é literal da decisão do Matheus.
 */
const AJUDA_TECNICA =
  'Quanto você vendeu no mês vigente, somando as duas empresas, pela data de emissão no Omie. ' +
  'Produtos e serviços pontuais do técnico e manutenção. Não inclui plantão nem faturamento direto de manutenção. ' +
  'Detalhes na Central de Ajuda, artigo "O card Minhas vendas".';

export function MinhasVendasWidget() {
  const { data, isLoading, isError } = useMinhasVendas();

  // Sem vínculo → sem card (e sem seção). A Home de quem não vende fica
  // exatamente como era.
  if (!isLoading && !isError && !data?.vinculado) return null;

  return (
    <section className="mb-6" data-testid="minhas-vendas-widget">
      <h2 className="eyebrow mb-3">Minhas vendas</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {isLoading ? (
          <KpiCard
            data-testid="minhas-vendas-carregando"
            label="Minhas vendas"
            value="…"
            context="buscando o total do mês"
          />
        ) : isError ? (
          <KpiCard
            data-testid="minhas-vendas-erro"
            label="Minhas vendas"
            value="—"
            context="Não consegui buscar suas vendas do mês. Recarregue a página; se continuar, avise um administrador."
          />
        ) : (
          <KpiCard
            data-testid="minhas-vendas-card"
            label={`Minhas vendas em ${mesPorExtenso(data?.anoMes)}`}
            value={
              <span className="[overflow-wrap:anywhere]">
                {formatCurrency((data?.valorCentavos ?? 0) / 100)}
              </span>
            }
            context={
              <span className="inline-flex items-center gap-1.5">
                mês vigente, pela data de emissão
                <HelpTip texto={data?.regra === 'tecnica' ? AJUDA_TECNICA : AJUDA} />
              </span>
            }
          />
        )}
      </div>
    </section>
  );
}
